import { resolveTargetKey } from './types';
import type {
  ConcatenateDefinition,
  ConcatenatePart,
  ConditionalDefinition,
  ConditionalMatch,
  ConditionalOutcome,
  FirstNonNullDefinition,
  FirstNonNullFormat,
  FixedValueDefinition,
  GenerationCohortDefinition,
  ImportRow,
  ReplaceDefinition,
  TenureRangeDefinition,
  Transformation,
  TransformationValidationResult,
} from './types';

/**
 * A transformation runs against the post-column-mapping row, whose keys are a mix of raw
 * (already-lowercased) HRIS headers and exact-case core-field names like `employeeId` that
 * `resolveTargetKey` never lowercases. Checking the exact-case key first lets a transformation
 * source from a mapped core field without a blanket lowercase hiding it.
 */
function readColumn(row: ImportRow, source: string): string | null {
  const value = source in row ? row[source] : row[source.toLowerCase()];
  return value === undefined ? null : value;
}

function isEmpty(value: string | null): boolean {
  return value == null || value === '';
}

const MONTH_DAY_PATTERN = /^\d{4}-(\d{2})-(\d{2})/;

/** Extracts `MMDD` from a `YYYY-MM-DD...` date string, or null when it doesn't match. */
function extractMonthDay(value: string): string | null {
  const match = value.match(MONTH_DAY_PATTERN);
  return match ? `${match[1]}${match[2]}` : null;
}

function formatConcatenatePart(part: ConcatenatePart, row: ImportRow): string {
  const raw = readColumn(row, part.source);

  if (part.format === 'extractMonthDay') {
    const extracted = isEmpty(raw) ? null : extractMonthDay(raw as string);
    return extracted ?? part.default ?? '';
  }

  if (isEmpty(raw)) {
    return part.default ?? '';
  }
  return raw as string;
}

function applyConcatenate(definition: ConcatenateDefinition, row: ImportRow): string {
  return definition.parts.map((part) => formatConcatenatePart(part, row)).join(definition.separator ?? '');
}

function formatSelectedValue(value: string, format: FirstNonNullFormat): string {
  return value.padStart(format.length, '0');
}

function applyFirstNonNull(definition: FirstNonNullDefinition, row: ImportRow): string | null {
  for (const candidate of definition.candidates) {
    const value = readColumn(row, candidate);
    if (!isEmpty(value)) {
      return definition.format ? formatSelectedValue(value as string, definition.format) : value;
    }
  }
  return null;
}

function matchesCondition(value: string | null, match: ConditionalMatch): boolean {
  if (value == null) {
    return false;
  }
  if (match.mode === 'equals') {
    return value === match.value;
  }
  if (match.mode === 'in') {
    return match.value.includes(value);
  }
  return value.startsWith(match.value);
}

function resolveConditionalOutcome(outcome: ConditionalOutcome, row: ImportRow): string | null {
  if (outcome.type === 'fixed') {
    return outcome.value;
  }
  return readColumn(row, outcome.source);
}

function applyConditional(definition: ConditionalDefinition, row: ImportRow): string | null {
  const sourceValue = readColumn(row, definition.source);
  const outcome = matchesCondition(sourceValue, definition.match) ? definition.whenTrue : definition.whenFalse;
  return resolveConditionalOutcome(outcome, row);
}

function applyReplace(definition: ReplaceDefinition, row: ImportRow): string | null {
  const value = readColumn(row, definition.source);
  if (value == null) {
    return null;
  }
  return value.replace(new RegExp(definition.pattern), definition.replacement);
}

function applyFixedValue(definition: FixedValueDefinition): string | null {
  return definition.value;
}

const FULL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/** Parses a `YYYY-MM-DD...` string into a local Date, or null when it doesn't match. */
function parseDate(value: string): Date | null {
  const match = value.match(FULL_DATE_PATTERN);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/** Bins matched in order (`value <= key`); `allElse` is returned when none match. */
function binValue(value: number, bins: [number, string][], allElse: string): string {
  for (const [key, label] of bins) {
    if (value <= key) return label;
  }
  return allElse;
}

/**
 * Fixed, company-agnostic tenure-length ranges, matching the legacy
 * pulse-employee-data-transformations `TenureRanges` table exactly.
 */
const TENURE_RANGE_BINS: [number, string][] = [
  [0, '0-1 year'],
  [2, '1-3 years'],
  [4, '3-5 years'],
  [9, '5-10 years'],
  [19, '10-20 years'],
  [29, '20-30 years'],
];
const TENURE_RANGE_ALL_ELSE = '30+ years';

function applyTenureRange(definition: TenureRangeDefinition, row: ImportRow, asOf: Date): string | null {
  const raw = readColumn(row, definition.source);
  if (isEmpty(raw)) return null;

  const startDate = parseDate(raw as string);
  if (!startDate) return null;

  const yearsPrecise = (asOf.getTime() - startDate.getTime()) / MS_PER_YEAR;
  if (yearsPrecise < 0) return null;

  return binValue(Math.floor(yearsPrecise), TENURE_RANGE_BINS, TENURE_RANGE_ALL_ELSE);
}

/**
 * Fixed, company-agnostic generational cohorts, matching the legacy
 * pulse-employee-data-transformations `GenerationalCohorts` table exactly.
 */
const GENERATION_COHORT_BINS: [number, string][] = [
  [1945, 'Traditionalists or Silent Generation: Born 1945 and before'],
  [1964, 'Baby Boomers: Born 1946 to 1964'],
  [1976, 'Generation X: Born 1965 to 1976'],
  [1995, 'Millennials or Gen Y: Born 1977 to 1995'],
];
const GENERATION_COHORT_ALL_ELSE = 'Gen Z, iGen, or Centennials: Born 1996 and later';

function resolveBirthYear(definition: GenerationCohortDefinition, raw: string, asOf: Date): number | null {
  if (definition.sourceType === 'birthDate') {
    return parseDate(raw)?.getFullYear() ?? null;
  }
  if (definition.sourceType === 'birthYear') {
    const birthYear = Number(raw);
    return Number.isNaN(birthYear) ? null : birthYear;
  }
  const age = Number(raw);
  return Number.isNaN(age) ? null : Math.floor(asOf.getFullYear() - age);
}

function applyGenerationCohort(definition: GenerationCohortDefinition, row: ImportRow, asOf: Date): string | null {
  const raw = readColumn(row, definition.source);
  if (isEmpty(raw)) return null;

  const birthYear = resolveBirthYear(definition, raw as string, asOf);
  if (birthYear == null) return null;

  return binValue(birthYear, GENERATION_COHORT_BINS, GENERATION_COHORT_ALL_ELSE);
}

/**
 * Computes one transformation's value against a raw parsed row. Every call receives the entire
 * row -- every column, unfiltered, including ones never mapped to any attribute -- and reads
 * only the specific columns its own `definition` names, via `source`/`candidates`/`parts`.
 * There is no separate step that figures out "which columns matter" ahead of time, and no
 * transformation knows or needs to know what any other transformation in a company's configured
 * list needs.
 *
 * `asOf` anchors TenureRange and age-based GenerationCohort transformations, which otherwise read
 * the real current time and can't be tested deterministically. Defaults to `new Date()` so
 * existing callers are unaffected.
 *
 * An unrecognized `transformType` returns `null` rather than `undefined`, matching this
 * function's return type -- the DB row it came from could have been inserted with a typo.
 */
export function applyTransformation(
  transformation: Transformation,
  row: ImportRow,
  asOf: Date = new Date(),
): string | null {
  switch (transformation.transformType) {
    case 'CONCATENATE':
      return applyConcatenate(transformation.definition, row);
    case 'FIRST_NON_NULL':
      return applyFirstNonNull(transformation.definition, row);
    case 'CONDITIONAL':
      return applyConditional(transformation.definition, row);
    case 'REPLACE':
      return applyReplace(transformation.definition, row);
    case 'FIXED_VALUE':
      return applyFixedValue(transformation.definition);
    case 'TENURE_RANGE':
      return applyTenureRange(transformation.definition, row, asOf);
    case 'GENERATION_COHORT':
      return applyGenerationCohort(transformation.definition, row, asOf);
    default:
      return null;
  }
}

/**
 * Applies a company's whole list of configured transformations to one raw row, producing a
 * record keyed by each transformation's resolved target (a core field, or an attribute's
 * lowercased name). The caller passes the same full row to every transformation; each one reads
 * only what its own `definition` names. A transformation targeting an attribute with no name for
 * the requesting user's language is skipped -- there's no key to write its result under.
 *
 * `asOf` defaults to `new Date()` once here and is passed to every transformation in the list, so
 * a batch resolves date-based transformations (TenureRange, age-based GenerationCohort) against
 * one consistent instant instead of drifting as the clock ticks mid-batch.
 */
export function applyTransformations(
  transformations: Transformation[],
  row: ImportRow,
  asOf: Date = new Date(),
): Record<string, string | null> {
  const result: Record<string, string | null> = {};
  for (const transformation of transformations) {
    const targetKey = resolveTargetKey(transformation);
    if (targetKey !== null) {
      result[targetKey] = applyTransformation(transformation, row, asOf);
    }
  }
  return result;
}

function transformationSources(transformation: Transformation): string[] {
  switch (transformation.transformType) {
    case 'CONCATENATE':
      return transformation.definition.parts.map((part) => part.source);
    case 'FIRST_NON_NULL':
      return transformation.definition.candidates;
    case 'CONDITIONAL': {
      const { source, whenTrue, whenFalse } = transformation.definition;
      return [source, whenTrue, whenFalse]
        .map((outcome) => (typeof outcome === 'string' ? outcome : outcome.type === 'source' ? outcome.source : null))
        .filter((value): value is string => value !== null);
    }
    case 'REPLACE':
      return [transformation.definition.source];
    case 'FIXED_VALUE':
      return [];
    case 'TENURE_RANGE':
      return [transformation.definition.source];
    case 'GENERATION_COHORT':
      return [transformation.definition.source];
    default:
      // A DB row with a corrupted or typo'd transformType has no known source columns, same as
      // FIXED_VALUE -- not a crash.
      return [];
  }
}

/**
 * Every raw column name a company's configured transformations read from, lowercased. Some of
 * these (e.g. a birthday column used only to derive another attribute) are never persisted as
 * their own attribute, so a caller validating a parsed row's columns against known attributes
 * needs this list to avoid flagging them as unrecognized.
 */
export function transformationSourceColumns(transformations: Transformation[]): string[] {
  const sources = new Set<string>();
  for (const transformation of transformations) {
    for (const source of transformationSources(transformation)) {
      sources.add(source.toLowerCase());
    }
  }
  return [...sources];
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function validateConcatenateDefinition(definition: unknown): string[] {
  const errors: string[] = [];
  if (typeof definition !== 'object' || definition === null || !Array.isArray((definition as any).parts)) {
    return ['Concatenate requires a "parts" array.'];
  }
  const parts = (definition as ConcatenateDefinition).parts;
  if (parts.length === 0) {
    errors.push('Concatenate requires at least one part.');
  }
  parts.forEach((part, index) => {
    if (!isNonEmptyString(part?.source)) {
      errors.push(`Concatenate part ${index} requires a "source".`);
    }
    if (part?.format !== undefined && part.format !== 'verbatim' && part.format !== 'extractMonthDay') {
      errors.push(`Concatenate part ${index} has an unrecognized "format".`);
    }
    if (part?.default !== undefined && typeof part.default !== 'string') {
      errors.push(`Concatenate part ${index}'s "default" must be a string.`);
    }
  });
  const separator = (definition as ConcatenateDefinition).separator;
  if (separator !== undefined && typeof separator !== 'string') {
    errors.push('Concatenate "separator" must be a string.');
  }
  return errors;
}

function validateFirstNonNullDefinition(definition: unknown): string[] {
  const errors: string[] = [];
  if (typeof definition !== 'object' || definition === null || !Array.isArray((definition as any).candidates)) {
    return ['FirstNonNull requires a "candidates" array.'];
  }
  const candidates = (definition as FirstNonNullDefinition).candidates;
  if (candidates.length === 0) {
    errors.push('FirstNonNull requires at least one candidate.');
  }
  candidates.forEach((candidate, index) => {
    if (!isNonEmptyString(candidate)) {
      errors.push(`FirstNonNull candidate ${index} must be a non-empty string.`);
    }
  });
  const format = (definition as FirstNonNullDefinition).format;
  if (format != null) {
    if (format.type !== 'zeroPad') {
      errors.push('FirstNonNull "format.type" must be "zeroPad".');
    }
    if (typeof format.length !== 'number' || format.length <= 0) {
      errors.push('FirstNonNull "format.length" must be a positive number.');
    }
  }
  return errors;
}

function validateConditionalOutcome(outcome: unknown, label: string): string[] {
  if (typeof outcome !== 'object' || outcome === null) {
    return [`Conditional "${label}" must be an object.`];
  }
  const candidate = outcome as ConditionalOutcome;
  if (candidate.type === 'fixed') {
    if (candidate.value !== null && typeof candidate.value !== 'string') {
      return [`Conditional "${label}.value" must be a string or null.`];
    }
    return [];
  }
  if (candidate.type === 'source') {
    if (!isNonEmptyString(candidate.source)) {
      return [`Conditional "${label}.source" must be a non-empty string.`];
    }
    return [];
  }
  return [`Conditional "${label}.type" must be "fixed" or "source".`];
}

function validateConditionalMatch(match: unknown): string[] {
  if (typeof match !== 'object' || match === null) {
    return ['Conditional "match" requires mode "equals", "startsWith", or "in".'];
  }
  const candidate = match as ConditionalMatch;
  if (candidate.mode === 'equals' || candidate.mode === 'startsWith') {
    return isNonEmptyString(candidate.value)
      ? []
      : ['Conditional "match" requires mode "equals" or "startsWith" and a non-empty "value".'];
  }
  if (candidate.mode === 'in') {
    return Array.isArray(candidate.value) && candidate.value.length > 0 && candidate.value.every(isNonEmptyString)
      ? []
      : ['Conditional "match" with mode "in" requires a non-empty array of non-empty string "value"s.'];
  }
  return ['Conditional "match" requires mode "equals", "startsWith", or "in".'];
}

function validateConditionalDefinition(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) {
    return ['Conditional requires an object definition.'];
  }
  const candidate = definition as ConditionalDefinition;
  const errors: string[] = [];
  if (!isNonEmptyString(candidate.source)) {
    errors.push('Conditional requires a "source".');
  }
  errors.push(...validateConditionalMatch(candidate.match));
  errors.push(...validateConditionalOutcome(candidate.whenTrue, 'whenTrue'));
  errors.push(...validateConditionalOutcome(candidate.whenFalse, 'whenFalse'));
  return errors;
}

function validateReplaceDefinition(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) {
    return ['Replace requires an object definition.'];
  }
  const candidate = definition as ReplaceDefinition;
  const errors: string[] = [];
  if (!isNonEmptyString(candidate.source)) {
    errors.push('Replace requires a "source".');
  }
  if (!isNonEmptyString(candidate.pattern)) {
    errors.push('Replace requires a "pattern".');
  } else if (!candidate.pattern.startsWith('^') && !candidate.pattern.endsWith('$')) {
    errors.push('Replace "pattern" must be anchored (start with "^" or end with "$") to avoid matching mid-string.');
  } else {
    try {
      new RegExp(candidate.pattern);
    } catch {
      errors.push('Replace "pattern" must be a valid regular expression.');
    }
  }
  if (typeof candidate.replacement !== 'string') {
    errors.push('Replace requires a "replacement" string.');
  }
  return errors;
}

function validateFixedValueDefinition(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) {
    return ['FixedValue requires an object definition.'];
  }
  const value = (definition as FixedValueDefinition).value;
  if (value !== null && typeof value !== 'string') {
    return ['FixedValue "value" must be a string or null.'];
  }
  return [];
}

function validateTenureRangeDefinition(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) {
    return ['TenureRange requires an object definition.'];
  }
  if (!isNonEmptyString((definition as TenureRangeDefinition).source)) {
    return ['TenureRange requires a "source".'];
  }
  return [];
}

const GENERATION_COHORT_SOURCE_TYPES = ['birthDate', 'birthYear', 'age'];

function validateGenerationCohortDefinition(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) {
    return ['GenerationCohort requires an object definition.'];
  }
  const errors: string[] = [];
  const candidate = definition as GenerationCohortDefinition;
  if (!isNonEmptyString(candidate.source)) {
    errors.push('GenerationCohort requires a "source".');
  }
  if (!GENERATION_COHORT_SOURCE_TYPES.includes(candidate.sourceType)) {
    errors.push('GenerationCohort "sourceType" must be "birthDate", "birthYear", or "age".');
  }
  return errors;
}

function validationErrorsForTransformType(transformType: string, definition: unknown): string[] {
  switch (transformType) {
    case 'CONCATENATE':
      return validateConcatenateDefinition(definition);
    case 'FIRST_NON_NULL':
      return validateFirstNonNullDefinition(definition);
    case 'CONDITIONAL':
      return validateConditionalDefinition(definition);
    case 'REPLACE':
      return validateReplaceDefinition(definition);
    case 'FIXED_VALUE':
      return validateFixedValueDefinition(definition);
    case 'TENURE_RANGE':
      return validateTenureRangeDefinition(definition);
    case 'GENERATION_COHORT':
      return validateGenerationCohortDefinition(definition);
    default:
      return [`Unrecognized transformType "${transformType}".`];
  }
}

/**
 * Validates a transformation's `definition` against its `transformType`'s expected shape.
 * Returns a typed result rather than throwing, so the API can turn failures into
 * `InvalidInputError` and the UI can render field-level errors -- mirroring
 * `validateRecipe`'s pattern for the existing dynamicAttributeRecipe engine.
 */
export function validateTransformationDefinition(
  transformType: string,
  definition: unknown,
): TransformationValidationResult {
  const errors = validationErrorsForTransformType(transformType, definition);

  return errors.length === 0 ? { valid: true, errors: [] } : { valid: false, errors };
}
